import React, {useState} from "react";
import {
    Alert,
    Anchor,
    Button,
    Container,
    Divider,
    LoadingOverlay,
    Paper,
    PasswordInput,
    TextInput,
    Title,
} from "@mantine/core";
import classes from "./Login.module.css";
import {useNavigate} from "react-router-dom";
import {AccountService} from "@bobex/react-core/services/AccountService";
import {useTranslation} from "react-i18next";
import AuthenticationRequest from "@bobex/react-core/domain/Authentication/AuthenticationRequestDTO";
import {
    GoogleOAuthProvider,
    GoogleLogin,
    CredentialResponse,
} from "@react-oauth/google";


export default function Login() {
    const GOOGLE_OAUTH_CLIENT_ID = window.location.hostname === "localhost"
            ? "433195828951-qgse6d208vbli7peeatm2s00hoha5ojp.apps.googleusercontent.com"
            : "433195828951-vakkracht0h2afshfsp1uafpbn6l6nnb.apps.googleusercontent.com";
    const {t} = useTranslation();
    const [credentials, setCredentials] = useState(new AuthenticationRequest());
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(window.location.search);

    function getErrorMessage(responseData: string): string {
        const [code, ip] = responseData.split("|");
        switch (code) {
            case "TOTP_REQUIRED":
                return (
                        t("TOTP code is required for remote access.") + (ip ? ` (${ip})` : "")
                );
            case "WRONG_TOTP":
                return t("Invalid TOTP code.");
            case "REMOTE_ACCESS_DENIED":
            case "IP_ACCESS_DENIED":
                return (
                        t("You do not have access from your IP address") +
                        (ip ? ` (${ip})` : "")
                );
            case "TOTP_NOT_CONFIGURED":
                return t(
                        "TOTP is not configured for your account. Contact an administrator.",
                );
            case "ACCESS_DENIED" :
                return (
                        t("Access denied for this account.")
                )
            case "TOO_MANY_ATTEMPTS" :
                return (
                        t("Your account has been blocked due to too many connection attempts.")
                )
            case "WRONG_LOGIN_DATA":
                return (t("The username or password you entered is incorrect."));
            case "USER_NOT_FOUND":
                return (t("Unable to find user with username provided!"))
            default:
                return responseData;
        }
    }

    function handleGoogleSuccess(credentialResponse: CredentialResponse) {
        if (!credentialResponse.credential) return;
        setError(null);
        setLoading(true);

        AccountService.googleLogin(credentialResponse.credential)
                .then((response) => {
                    AccountService.saveToken(response.accessToken);
                    AccountService.saveRefreshToken(response.refreshToken);
                    AccountService.saveUser(response);
                    // Check if this is a redirect from SSO external application
                    const redirect = searchParams.get("redirect");
                    const app = searchParams.get("app");

                    //temporary , during legacy / rebo transitions
                    if (redirect === "sso" && app) {
                        // Redirect back to SSO redirect page to continue to external app
                        navigate(`/sso-redirect?app=${app}`);
                        return;
                    }

                    const redirectTo = AccountService.getRedirectAfterLogin();
                    if (redirectTo) {
                        localStorage.removeItem("redirectAfterLogin");
                        window.location.href = redirectTo;
                    } else {
                        navigate("/dashboard");
                    }
                })
                .catch((err) => {
                    const data: string = typeof err?.response?.data === "string" ? err?.response?.data : t("Google Sign-In failed.")
                    if (data.toString().includes("REMOTE_ACCESS_DENIED")) {
                        setError(getErrorMessage(typeof data === "string" ? data : ""));
                    } else {
                        setError(data);
                    }
                })
                .finally(() => setLoading(false));
    }

    function submitLogin(event: React.FormEvent) {
        event.preventDefault();
        setError(null);
        setLoading(true);

        AccountService.login(credentials)
                .then((response) => {
                    AccountService.saveToken(response.accessToken);
                    AccountService.saveRefreshToken(response.refreshToken);
                    AccountService.saveUser(response);

                    // Check if this is a redirect from SSO external application
                    const redirect = searchParams.get("redirect");
                    const app = searchParams.get("app");

                    //temporary , during legacy / rebo transitions
                    if (redirect === "sso" && app) {
                        // Redirect back to SSO redirect page to continue to external app
                        navigate(`/sso-redirect?app=${app}`);
                        return;
                    }

                    const redirectTo = AccountService.getRedirectAfterLogin();
                    if (redirectTo) {
                        localStorage.removeItem("redirectAfterLogin");
                        window.location.href = redirectTo;
                    } else {
                        navigate("/dashboard");
                    }
                })
                .catch((err) => {
                    const data = err?.response?.data;

                    setError(getErrorMessage(typeof data === "string" ? data : ""));
                })
                .finally(() => setLoading(false));
    }

    return (
            <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
                <Container size={420} my={40}>
                    <Title ta="center" className={classes.title}>
                        {t("BackOffice Edition")}
                    </Title>
                    <Paper withBorder shadow="md" p={30} mt={30} radius="md" pos="relative">
                        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{radius: "md", blur: 2}}/>
                        <form onSubmit={submitLogin}>
                            {error && (
                                    <Alert color="red" mb="md">
                                        {error}
                                    </Alert>
                            )}
                            <TextInput
                                    label={t("Email or username")}
                                    placeholder={t("Username")}
                                    required
                                    onChange={(e) =>
                                            setCredentials({...credentials, loginId: e.target.value})
                                    }
                            />
                            <PasswordInput
                                    label={t("Password")}
                                    placeholder={t("Password")}
                                    required
                                    mt="md"
                                    onChange={(e) =>
                                            setCredentials({...credentials, password: e.target.value})
                                    }
                            />
                            <TextInput
                                    label={t("Code")}
                                    placeholder={t("Code")}
                                    mt="md"
                                    onChange={(e) =>
                                            setCredentials({...credentials, totp: e.target.value})
                                    }
                            />
                            <Anchor component="button" size="sm" mt="lg" type="button">
                                {t("Forgot password?")}
                            </Anchor>
                            <Button fullWidth mt="xl" type="submit" loading={loading}>
                                {t("Sign in")}
                            </Button>
                        </form>

                        <Divider label={t("Or")} labelPosition="center" my="lg"/>

                        <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => setError(t("Google Sign-In failed."))}
                        />
                    </Paper>
                </Container>
            </GoogleOAuthProvider>
    );
}
